import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import AddressAutocomplete from '../components/ui/address-autocomplete';
import { CheckCircle2, User, Briefcase, Package } from 'lucide-react';
import { authAPI, jobsAPI } from '../utils/api';

const SetupWizardPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(JSON.parse(localStorage.getItem('movemate_user') || '{}'));
  
  const [profileData, setProfileData] = useState({
    first_name: userData.first_name || '',
    last_name: userData.last_name || '',
    phone: userData.phone || '',
    email: userData.email || '',
  });

  const [jobData, setJobData] = useState({
    customer_type: 'domestic',
    from_address: { street: '', city: '', postal_code: '', country: 'UK' },
    to_address: { street: '', city: '', postal_code: '', country: 'UK' },
    move_out_date: '',
    move_in_date: '',
    special_instructions: '',
  });

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const handleProfileSubmit = async () => {
    setLoading(true);
    try {
      // Update profile via API (you'll need to add this endpoint)
      toast.success('Profile updated!');
      setStep(2);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleJobSubmit = async () => {
    setLoading(true);
    try {
      await jobsAPI.create(jobData);
      toast.success('Job created!');
      setStep(3);
    } catch (error) {
      toast.error('Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipToInventory = () => {
    setStep(3);
  };

  const handleComplete = () => {
    toast.success('Setup complete! Welcome to MoveMate');
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-8" data-testid="setup-wizard">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold gradient-text">Setup Your Account</h1>
            <span className="text-sm text-gray-600">Step {step} of {totalSteps}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Indicators */}
        <div className="flex justify-between mb-8">
          <StepIndicator icon={<User size={20} />} title="Profile" active={step === 1} completed={step > 1} />
          <StepIndicator icon={<Briefcase size={20} />} title="Job Details" active={step === 2} completed={step > 2} />
          <StepIndicator icon={<Package size={20} />} title="Inventory" active={step === 3} completed={false} />
        </div>

        {/* Step 1: Profile */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Check & Edit Your Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={profileData.first_name}
                      onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                      data-testid="profile-first-name"
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={profileData.last_name}
                      onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                      data-testid="profile-last-name"
                    />
                  </div>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    data-testid="profile-phone"
                  />
                </div>
                <div>
                  <Label>Email (Optional)</Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    data-testid="profile-email"
                  />
                </div>
                <Button onClick={handleProfileSubmit} className="w-full" disabled={loading}>
                  {loading ? 'Saving...' : 'Continue to Job Details'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Job Details */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Create Your Moving Job</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">From Address</h3>
                  <div className="space-y-4">
                    <AddressAutocomplete
                      value={jobData.from_address.street}
                      onAddressSelect={(addr) => setJobData({
                        ...jobData,
                        from_address: { 
                          ...jobData.from_address, 
                          street: addr.street,
                          city: addr.city || jobData.from_address.city,
                          postal_code: addr.postal_code || jobData.from_address.postal_code
                        }
                      })}
                      onChange={(e) => setJobData({
                        ...jobData,
                        from_address: { ...jobData.from_address, street: e.target.value }
                      })}
                      placeholder="Start typing your current address..."
                    />
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        placeholder="City"
                        value={jobData.from_address.city}
                        onChange={(e) => setJobData({
                          ...jobData,
                          from_address: { ...jobData.from_address, city: e.target.value }
                        })}
                      />
                      <Input
                        placeholder="Postal Code"
                        value={jobData.from_address.postal_code}
                        onChange={(e) => setJobData({
                          ...jobData,
                          from_address: { ...jobData.from_address, postal_code: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">To Address</h3>
                  <div className="space-y-4">
                    <AddressAutocomplete
                      value={jobData.to_address.street}
                      onAddressSelect={(addr) => setJobData({
                        ...jobData,
                        to_address: { 
                          ...jobData.to_address, 
                          street: addr.street,
                          city: addr.city || jobData.to_address.city,
                          postal_code: addr.postal_code || jobData.to_address.postal_code
                        }
                      })}
                      onChange={(e) => setJobData({
                        ...jobData,
                        to_address: { ...jobData.to_address, street: e.target.value }
                      })}
                      placeholder="Start typing your new address..."
                    />
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        placeholder="City"
                        value={jobData.to_address.city}
                        onChange={(e) => setJobData({
                          ...jobData,
                          to_address: { ...jobData.to_address, city: e.target.value }
                        })}
                      />
                      <Input
                        placeholder="Postal Code"
                        value={jobData.to_address.postal_code}
                        onChange={(e) => setJobData({
                          ...jobData,
                          to_address: { ...jobData.to_address, postal_code: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Move Out Date</Label>
                    <Input
                      type="date"
                      value={jobData.move_out_date}
                      onChange={(e) => setJobData({ ...jobData, move_out_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Move In Date</Label>
                    <Input
                      type="date"
                      value={jobData.move_in_date}
                      onChange={(e) => setJobData({ ...jobData, move_in_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button onClick={handleSkipToInventory} variant="outline" className="flex-1">
                    Skip for Now
                  </Button>
                  <Button onClick={handleJobSubmit} className="flex-1" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Job'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Inventory */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Inventory Options</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-600 mb-6">
                  Choose how you would like to create your inventory
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-32 flex flex-col gap-2" onClick={() => navigate('/inventory/ai')}>
                    <Package size={32} className="text-primary" />
                    <span className="font-semibold">AI Scan</span>
                    <span className="text-xs text-gray-600">Take photos, AI identifies items</span>
                  </Button>

                  <Button variant="outline" className="h-32 flex flex-col gap-2" onClick={() => navigate('/inventory/manual')}>
                    <Package size={32} className="text-primary" />
                    <span className="font-semibold">Pre-made List</span>
                    <span className="text-xs text-gray-600">Choose from templates</span>
                  </Button>
                </div>

                <Button onClick={handleComplete} className="w-full" variant="outline">
                  Skip & Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const StepIndicator = ({ icon, title, active, completed }) => {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
        completed ? 'bg-green-500 text-white' : active ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
      }`}>
        {completed ? <CheckCircle2 size={24} /> : icon}
      </div>
      <span className={`text-sm font-medium ${
        active ? 'text-primary' : completed ? 'text-green-600' : 'text-gray-500'
      }`}>{title}</span>
    </div>
  );
};

export default SetupWizardPage;
